import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  DatePickerField,
  DropdownField,
  FormConfig,
  FormRow,
  RadioField,
  TimeField,
  AccordionMultiSelect,
  AccordionMultiSelectItem,
} from '@frontend/design-system';
import { FormWrapperChildrenProps } from '../form-wrapper';
import { FilterSectionHeader } from './filter-section-header';
import { ExceptionRecurrenceFilter, ExceptionStatus } from './use-rule-exceptions-filters';

const STATUS_OPTIONS: { value: ExceptionStatus; label: string }[] = [
  { value: 'current', label: 'Current Exceptions' },
  { value: 'expired', label: 'Expired Exceptions' },
];

const RECURRENCE_OPTIONS: { value: ExceptionRecurrenceFilter; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'SPECIFIC_DATE', label: 'Specific Date' },
];

export const filterFormFields = {
  providerIds: { type: 'checklist', value: [] },
  serviceIds: { type: 'checklist', value: [] },
  resourceIds: { type: 'checklist', value: [] },
  startTime: { type: 'time', value: '' },
  endTime: { type: 'time', value: '' },
  startDate: { type: 'datePicker', value: '' },
  endDate: { type: 'datePicker', value: '' },
  status: { type: 'radio', value: STATUS_OPTIONS[0].value },
  recurrence: { type: 'dropdown', value: '' },
} satisfies FormConfig;

export type FilterFormFields = typeof filterFormFields;

export interface RuleExceptionsFilterFormProps {
  getFieldProps: FormWrapperChildrenProps<FilterFormFields>['getFieldProps'];
  providerItems: AccordionMultiSelectItem[];
  serviceItems: AccordionMultiSelectItem[];
  operatoryItems: AccordionMultiSelectItem[];
  onClearExceptionTarget: () => void;
  onClearTimeFrame: () => void;
  onClearView: () => void;
  onClearRecurrence: () => void;
}

export const RuleExceptionsFilterForm = ({
  getFieldProps,
  providerItems,
  serviceItems,
  operatoryItems,
  onClearExceptionTarget,
  onClearTimeFrame,
  onClearView,
  onClearRecurrence,
}: RuleExceptionsFilterFormProps) => {
  const { t } = useTranslation('schedule-settings');

  return (
    <>
      {/* Exception Target */}
      <FilterSectionHeader
        title={t('Exception Target')}
        onClear={onClearExceptionTarget}
        noFiltersApplied={
          !getFieldProps('providerIds').value.length &&
          !getFieldProps('serviceIds').value.length &&
          !getFieldProps('resourceIds').value.length
        }
      />

      <AccordionMultiSelect>
        {providerItems.length > 0 && (
          <AccordionMultiSelect.Item
            title={t('Providers')}
            iconName='user-small'
            items={providerItems}
            fieldProps={{ ...getFieldProps('providerIds'), name: 'providerIds' }}
          />
        )}
        {operatoryItems.length > 0 && (
          <AccordionMultiSelect.Item
            title={t('Operatories')}
            iconName='department-small'
            items={operatoryItems}
            fieldProps={{ ...getFieldProps('resourceIds'), name: 'resourceIds' }}
          />
        )}
        {serviceItems.length > 0 && (
          <AccordionMultiSelect.Item
            title={t('Services')}
            iconName='tile-small'
            items={serviceItems}
            fieldProps={{ ...getFieldProps('serviceIds'), name: 'serviceIds' }}
          />
        )}
      </AccordionMultiSelect>

      {/* Time Frame */}
      <FilterSectionHeader
        title={t('Time Frame')}
        onClear={onClearTimeFrame}
        noFiltersApplied={
          !getFieldProps('startTime').value &&
          !getFieldProps('endTime').value &&
          !getFieldProps('startDate').value &&
          !getFieldProps('endDate').value
        }
      />
      <FormRow>
        <TimeField {...getFieldProps('startTime')} label={t('Start Time')} showAdornment css={{ flex: 1 }} />
        <TimeField {...getFieldProps('endTime')} label={t('End Time')} showAdornment css={{ flex: 1 }} />
      </FormRow>
      <FormRow css={{ marginTop: theme.spacing(2) }}>
        <DatePickerField {...getFieldProps('startDate')} label={t('Start Date')} css={{ flex: 1 }} />
        <DatePickerField {...getFieldProps('endDate')} label={t('End Date')} css={{ flex: 1 }} />
      </FormRow>

      {/* View/Status */}
      <FilterSectionHeader
        title={t('View')}
        onClear={onClearView}
        noFiltersApplied={getFieldProps('status').value === STATUS_OPTIONS[0].value}
      />
      <FormRow css={sectionStyle}>
        <RadioField {...getFieldProps('status')} label={''}>
          {STATUS_OPTIONS.map((opt) => (
            <RadioField.Radio key={opt.value} value={opt.value}>
              {t(opt.label)}
            </RadioField.Radio>
          ))}
        </RadioField>
      </FormRow>

      {/* Recurrence */}
      <FilterSectionHeader
        title={t('Recurrence Type')}
        onClear={onClearRecurrence}
        noFiltersApplied={!getFieldProps('recurrence').value}
      />
      <FormRow css={sectionStyle}>
        <DropdownField {...getFieldProps('recurrence')} label={t('Recurrence Type')}>
          {RECURRENCE_OPTIONS.map((opt) => (
            <DropdownField.Option key={opt.value} value={opt.value}>
              {t(opt.label)}
            </DropdownField.Option>
          ))}
        </DropdownField>
      </FormRow>
    </>
  );
};

const sectionStyle = { marginBottom: theme.spacing(3) };
